/**
 * @fileoverview Theme provider for managing light/dark mode across the application.
 * @module shared/providers/ThemeProvider
 */

import { ReactElement, ReactNode, useEffect, useMemo } from 'react'
import { ThemeProviderContext, type Theme } from '../contexts/ThemeContext'

interface ThemeProviderProps {
  children: ReactNode
}

/**
 * Theme provider component that manages theme state.
 * Enforces dark mode throughout the application.
 *
 * @component
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
}: ThemeProviderProps): ReactElement {
  // Always force dark theme
  const theme: Theme = 'dark'

  useEffect(() => {
    const root = window.document.documentElement
    // Ensure root class is clean and has only 'dark' if needed, though we now use :root vars directly
    // Keeping 'dark' class for backward compatibility with any specific .dark selectors
    root.classList.remove('light', 'system') 
    root.classList.add('dark')
    root.setAttribute('data-theme', 'dark')
  }, [])

  const value = useMemo(
    () => ({
      theme,
      setTheme: () => {
        // No-op: Theme is locked to dark
        console.warn('Theme switching is disabled. Dark mode is enforced.')
      },
    }),
    [theme]
  )

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>
}
